import React, { useEffect, useState, useCallback } from 'react';
import NumberInput from '../../components/NumberInput';
import { useTooltipNonmetallic } from './useTooltipNonmetallic';
import { FaTelegramPlane, FaComment } from 'react-icons/fa';
import { CircularProgress, IconButton } from '@mui/material';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import DeliveryAlternativesList from './DeliveryAlternativesList';
import SandAnalogsList from './SandAnalogsList';

const formatQuantity = (quantity) => {
  const num = parseFloat(quantity);
  if (isNaN(num)) {
    return quantity;
  }
  const hasDecimals = num % 1 !== 0;
  return hasDecimals ? num.toFixed(2) : num.toString();
};

const OfferTableNonmetallic = ({
  offer,
  offerType,
  activeOffers,
  selectedOffer,
  localPrices,
  localDeliveryPrice,
  localSkidka,
  localSkidkaD,
  localGlobalSkidka,
  per,
  tableContainerRef,
  onMaterialPriceChange,
  onSkidkaChange,
  onDeliveryPriceChange,
  onSkidkaChangeDelivery,
  onGlobalSkidkaChange,
  onSkidkaOrMarzha,
  onFillBitrix,
  onSendToTelegram,
  onSendToMax,
  onCreateMessage,
  setTooltip,
  data,
  title,
  selectedProduction,
  onRemoveMaterial,
  paymentMethod,
  addressAndCoordinates,
  // Новые пропсы для синхронизации с ProductionsNonmetallic
  materialDeliveryMethods = {},
  materialDeliveryData = {},
  onMaterialDeliveryMethodChange,
  // Информация о производстве (адрес, время работы)
  productionInfo,
  // Пропсы для работы с аналогами песка
  sandAnalogs = [],
  isLoadingAnalogs = false,
  findAnalogsForSand,
  handleSelectAnalog,
}) => {
  const isOriginal = offerType === 'original';
  const { handleMouseEnter, handleMouseLeave } = useTooltipNonmetallic(setTooltip, tableContainerRef, selectedOffer, productionInfo);
  const [source, setSource] = useState(null);
  const [materialDeliveryAlternatives, setMaterialDeliveryAlternatives] = useState({}); // Альтернативы для каждого материала
  const [isLoadingMaterialAlternatives, setIsLoadingMaterialAlternatives] = useState({});
  // Состояние для управления видимостью аналогов песка
  const [showSandAnalogs, setShowSandAnalogs] = useState(false);

  const resultSkidkaOrMarzha = (realSum, bazeSum, id) => {
    const parsedRealSum = Math.round(parseFloat(realSum) || 0);
    const parsedBazeSum = Math.round(parseFloat(bazeSum) || 0);
    if (parsedBazeSum === 0) return 0;
    let result = 0;
    if (id === 1) result = parsedBazeSum - parsedRealSum;
    else result = parsedRealSum - parsedBazeSum;
    return result;
  };

  const procentSkidkaOrMarzha = (realSum, bazeSum, id) => {
    const parsedRealSum = Math.round(parseFloat(realSum) || 0);
    const parsedBazeSum = Math.round(parseFloat(bazeSum) || 0);
    if (parsedBazeSum === 0) return 0;
    let result = 0;
    if (id === 1) result = Math.round(100 - (parsedRealSum / parsedBazeSum) * 100);
    else result = Math.round((parsedRealSum / parsedBazeSum - 1) * 100);
    return parseInt(Math.abs(result));
  };

  // Категории керамзита считаются отдельным калькулятором (routes/deliveryKeramzitCalculator.js),
  // а не общей нерудной формулой — их нельзя схлопывать в один "Керамзит навалом".
  const KERAMZIT_CATEGORIES = new Set([
    'Керамзит навалом',
    'Керамзит в мешках',
    'Керамзит в Биг-Бэгах',
  ]);

  // Функция для определения подкатегории материала
  const getMaterialSubcategory = (materialName) => {
    const name = materialName.toLowerCase();

    if (name.includes('песок')) return 'Песок';
    if (name.includes('щебень') && name.includes('известняк')) return 'Щебень известняковый';
    if (name.includes('щебень') && name.includes('гранит')) return 'Щебень гранитный';
    if (name.includes('щебень') && name.includes('гравий')) return 'Щебень гравийный';
    if (name.includes('щебень') && name.includes('вторичный')) return 'Щебень вторичный';
    if (name.includes('щпс')) return 'ШПС';
    if (name.includes('пгс')) return 'ПГС';
    if (name.includes('булыжник') || name.includes('бутовый')) return 'Бутовый камень';
    if (name.includes('керамзит')) return 'Керамзит навалом';

    // По умолчанию возвращаем первое слово как категорию
    return materialName.split(' ')[0];
  };

  // Функция для загрузки/скрытия альтернативных способов доставки для конкретного материала
  const toggleMaterialDeliveryAlternatives = async (materialIndex) => {
    const material = offer.materials[materialIndex];
    if (!material) return;

    const materialKey = `${materialIndex}_${material.name}`;

    // Если альтернативы уже загружены, скрываем их
    if (materialDeliveryAlternatives[materialKey]) {
      setMaterialDeliveryAlternatives(prev => {
        const newState = { ...prev };
        delete newState[materialKey];
        return newState;
      });
      return;
    }

    setIsLoadingMaterialAlternatives(prev => ({
      ...prev,
      [materialKey]: true
    }));

    try {
      // material.category приходит из Bitrix и различает мешки/биг-бэг/навалом;
      // getMaterialSubcategory — эвристика по имени, которая всегда даёт "Керамзит
      // навалом" для любого керамзита, поэтому доверяем ей только когда category
      // не является уже известной керамзитной категорией.
      const subcategory = KERAMZIT_CATEGORIES.has(material.category)
        ? material.category
        : getMaterialSubcategory(material.name);

      const effectiveAddressAndCoordinates = addressAndCoordinates || {
        coordinates: { latitude: 59.9343, longitude: 30.3351 },
        address: "Санкт-Петербург"
      };
      const production = material.manufacturer || offer.production || selectedProduction;

      let alternatives = null;

      if (KERAMZIT_CATEGORIES.has(subcategory)) {
        const orderVolume = (Number(material.quantity) || 0) * (Number(material.volumePerOne || material.volume) || 1);

        // Список всех подходящих машин для этой категории+производства (не только
        // лучшей) — источник правды: keramzit_loading_norms (Шушары) или
        // delivery_nerud_vehicles (Калуга, одна фикс. машина). Щеповоз/самосвалы сюда
        // попасть не могут в принципе — их нет в этих справочниках для мешков/биг-бэгов.
        const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/deliveryAlternativesKeramzit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            category: subcategory,
            production,
            orderVolume,
            // Количество единиц товара: по нему сервер считает рейсы для мешков из
            // Калуги (кратно фуре), где вместимость задана в мешках, а не в кубах.
            orderQuantity: Number(material.quantity) || 0,
            // Название позиции — из него сервер берёт фракцию керамзита для норм
            // погрузки (keramzit_loading_norms), см. calculateDeliveryNonmetallic.js.
            nomenclatures: [material.name].filter(Boolean),
            addressAndCoordinates: {
              ...effectiveAddressAndCoordinates,
              region: selectedOffer?.region || "СПБ",
            },
          })
        });

        if (response.ok) {
          const { data } = await response.json();
          alternatives = Array.isArray(data) ? data : [];
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error("❌ Ошибка при загрузке альтернатив доставки керамзита:", response.status, errorData.error);
        }
      } else {
        const requestData = {
          materials: [{
            name: material.name,
            category: subcategory,
            quantity: material.quantity,
            volumePerOne: material.volumePerOne || material.volume || 1,
            weight: material.weight || material.massPerOne || 0,
            manufacturer: production
          }],
          addressAndCoordinates: effectiveAddressAndCoordinates,
          selectedRegion: selectedOffer?.region || "СПБ",
          paymentMethod: paymentMethod || "cash"
        };

        console.log("🚚 Загрузка альтернативных доставок для материала:", material.name, "подкатегория:", subcategory, requestData);

        const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/deliveryAlternativesNer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            alternatives = data.data;
          }
        } else {
          const errorText = await response.text();
          console.error("❌ Ошибка при загрузке альтернативных доставок:", response.status, errorText);
        }
      }

      console.log("🚚 Получены альтернативные доставки для материала:", material.name, alternatives);

      if (alternatives && alternatives.length > 0) {
        setMaterialDeliveryAlternatives(prev => ({
          ...prev,
          [materialKey]: alternatives
        }));
      } else {
        console.warn("⚠️ Не найдено вариантов доставки для материала:", material.name);
      }
    } catch (error) {
      console.error('❌ Ошибка при загрузке альтернативных доставок для материала:', error);
    } finally {
      setIsLoadingMaterialAlternatives(prev => ({
        ...prev,
        [materialKey]: false
      }));
    }
  };

  // Функция для обработки изменения способа доставки материала
  const handleMaterialDeliveryChange = useCallback((materialIndex, selectedAlternative) => {
    const material = offer.materials[materialIndex];
    console.log('🚚 OfferTableNonmetallic: выбор способа доставки для материала', material?.name, ':', selectedAlternative);

    if (onMaterialDeliveryMethodChange && material) {
      onMaterialDeliveryMethodChange(material.name, selectedAlternative.name, selectedAlternative);
    }

    // Пересчитываем цену материала: (базовая цена материала * количество + доставка) / количество
    if (onMaterialPriceChange && selectedAlternative.totalCost && material.quantity) {
      // Используем базовую цену материала без доставки
      const baseMaterialPrice = material.basePricePerOne || material.pricePerOne || 0;
      const materialCostOnly = baseMaterialPrice * material.quantity; // Стоимость только материала
      const totalCostWithDelivery = materialCostOnly + selectedAlternative.totalCost; // Материал + доставка
      const newPricePerUnit = totalCostWithDelivery / material.quantity; // Новая цена за единицу

      console.log('💰 Пересчет цены материала:', {
        materialName: material.name,
        baseMaterialPrice,
        materialCostOnly,
        deliveryCost: selectedAlternative.totalCost,
        totalCostWithDelivery,
        quantity: material.quantity,
        newPricePerUnit
      });

      onMaterialPriceChange(materialIndex, newPricePerUnit, offerType);
    }
  }, [offer.materials, onMaterialDeliveryMethodChange, onMaterialPriceChange, localPrices, offerType]);

  // Получение способа доставки для конкретного материала
  const getMaterialDeliveryMethod = useCallback((materialIndex) => {
    const materialName = offer.materials[materialIndex]?.name;
    const material = offer.materials[materialIndex];

    console.log('🚚 DEBUG getMaterialDeliveryMethod:', {
      materialName,
      savedMethod: materialDeliveryMethods[materialName],
      materialDeliveryMethod: material?.deliveryMethod,
      realDeliveryMethod: material?.realDeliveryMethod,
      offerDeliveryName: offer.delivery?.name
    });

    // Сначала проверяем materialDeliveryMethods (выбранные альтернативы)
    const savedMethod = materialDeliveryMethods[materialName];
    if (savedMethod && savedMethod !== 'Оптимальный маршрут') {
      console.log('🚚 ВОЗВРАТ savedMethod:', savedMethod);
      return savedMethod;
    }

    // Проверяем deliveryMethod прямо в материале
    if (material?.deliveryMethod && material.deliveryMethod !== 'Оптимальный маршрут') {
      console.log('🚚 ВОЗВРАТ material.deliveryMethod:', material.deliveryMethod);
      return material.deliveryMethod;
    }

    // Если выбран "Оптимальный маршрут", берем реальный метод доставки
    if (material?.realDeliveryMethod && material.realDeliveryMethod !== 'Самовывоз') {
      console.log('🚚 ВОЗВРАТ material.realDeliveryMethod:', material.realDeliveryMethod);
      return material.realDeliveryMethod;
    }

    // Затем проверяем способ доставки из offer (изначальный метод для нерудки)
    if (offer.delivery?.name && offer.delivery.name !== 'Самовывоз') {
      console.log('🚚 ВОЗВРАТ offer.delivery.name:', offer.delivery.name);
      return offer.delivery.name;
    }

    // Если нет доставки, показываем что нужно выбрать метод
    console.log('🚚 ВОЗВРАТ дефолт: Выберите способ доставки');
    return 'Выберите способ доставки';
  }, [offer.materials, materialDeliveryMethods, offer.delivery]);

  // Функция для показа/скрытия аналогов песка
  const toggleSandAnalogs = async (materialIndex) => {
    const material = offer.materials[materialIndex];
    if (!material) return;

    if (!showSandAnalogs) {
      // Если аналоги скрыты, показываем их и загружаем
      console.log('🔍 Поиск аналогов для материала:', material.name);
      setShowSandAnalogs(true);
      if (findAnalogsForSand) {
        await findAnalogsForSand(material);
      }
    } else {
      // Если аналоги показаны, скрываем их
      setShowSandAnalogs(false);
    }
  };

  useEffect(() => {
    // Собираем все уникальные источники из материалов
    const allSources = offer.materials.map(material => {
      const foundItem = data.find(item =>
        item.product_calculator_name === material.name &&
        item.manufacturer === material.manufacturer
      );
      return foundItem && (foundItem.source ? foundItem.source : foundItem.source_table);
    }).filter(Boolean);

    const uniqueSources = [...new Set(allSources)];
    console.log('All sources:', allSources, 'Unique sources:', uniqueSources);

    // Показываем источник только если он один для всех материалов
    if (uniqueSources.length === 1) {
      setSource(uniqueSources[0]);
    } else {
      setSource(null); // Скрываем источник если их несколько
    }
  }, [activeOffers, data, offer.materials]);

  const totalSum = offer.materials.reduce((sum, mat) => sum + (parseInt(mat.sum) || 0), 0);
  const baseSum = offer.materials.reduce((sum, mat) => sum + (parseInt(mat.sum) || 0), 0);

  if (!isOriginal && !source) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <div className="rd-kp-table-wrapper" ref={tableContainerRef}>
        <div className="rd-kp-group">
          {title || (isOriginal ? 'Исходное предложение' : 'Аналог')}
          {!isOriginal && source && <small className="rd-kp-warn">Источник: {source}</small>}
        </div>

        <div>
          <div>
            <table className="rd-kp-table">
              <thead>
                <tr>
                  <th>№</th>
                  <th className="rd-kp-th--text">Наименование</th>
                  <th>Кол-во</th>
                  <th>Ед.</th>
                  <th>Цена / ед.</th>
                  <th>Сумма</th>
                  <th className="rd-kp-th--text">Способ доставки</th>
                  {isOriginal && (
                    <th
                      style={{ textDecoration: 'underline', color: 'var(--rd-blue-600)', cursor: 'pointer' }}
                      onClick={onSkidkaOrMarzha}
                    >
                      {per.text}
                    </th>
                  )}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {offer.materials.map((material, index) => (
                  <React.Fragment key={material.name + index}>
                    <tr>
                      <td className="text-center">{index + 1}</td>
                      <td
                        className="rd-kp-td--text"
                        onMouseEnter={(e) => handleMouseEnter(e, material)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span>{material.name}</span>
                          {/* Кнопка аналогов песка - показываем только если есть аналоги */}
                          {material.name.toLowerCase().includes('песок') && isOriginal &&
                            sandAnalogs[material.name]?.length > 0 && (
                              <button
                                className="rd-kp-inline-btn"
                                onClick={() => toggleSandAnalogs(index)}
                              >
                                {showSandAnalogs ? (
                                  <>Скрыть<MdExpandLess size={13} /></>
                                ) : (
                                  <>Аналоги ({sandAnalogs[material.name].length})<MdExpandMore size={13} /></>
                                )}
                              </button>
                            )}
                        </div>
                      </td>
                      <td className="text-center">{formatQuantity(material.quantity)}</td>
                      <td className="text-center">{material.unit}</td>
                      <td className="text-center">
                        <NumberInput
                          value={isOriginal ? Math.ceil(localPrices[index]) : Math.ceil(material.pricePerOne)}
                          onChange={(value) => onMaterialPriceChange(index, value, offerType)}
                          style={{ width: '100px' }}
                        />
                      </td>
                      <td className="text-center">{Math.ceil(material.sum).toLocaleString('ru-RU')} руб.</td>
                      <td className="text-center">
                        {isOriginal ? (
                          <button
                            onClick={() => toggleMaterialDeliveryAlternatives(index)}
                            disabled={isLoadingMaterialAlternatives[`${index}_${material.name}`]}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #1976d2',
                              backgroundColor: 'transparent',
                              color: '#1976d2',
                              fontSize: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              minWidth: '120px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {isLoadingMaterialAlternatives[`${index}_${material.name}`] ? (
                              <CircularProgress size={16} />
                            ) : (
                              <>
                                {getMaterialDeliveryMethod(index)}
                                {materialDeliveryAlternatives[`${index}_${material.name}`] ? (
                                  <MdExpandLess size={16} style={{ marginLeft: '4px' }} />
                                ) : (
                                  <MdExpandMore size={16} style={{ marginLeft: '4px' }} />
                                )}
                              </>
                            )}
                          </button>
                        ) : (
                          <span style={{ fontSize: '12px' }}>
                            {getMaterialDeliveryMethod(index)}
                          </span>
                        )}
                      </td>
                      {isOriginal && (
                        <td className="text-center">
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <NumberInput
                              value={localSkidka[index] || ''}
                              onChange={(value) => onSkidkaChange(index, value, offerType)}
                              style={{ width: '80px' }}
                            />
                            <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>%</span>
                          </div>
                        </td>
                      )}
                      <td className="text-center">
                        {offer.materials.length > 1 && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onRemoveMaterial(index, material)}
                            title="Удалить материал"
                          >
                            <span style={{ fontSize: '16px' }}>✕</span>
                          </IconButton>
                        )}
                      </td>
                    </tr>
                    {/* Показываем альтернативы доставки для материала, если они загружены */}
                    {materialDeliveryAlternatives[`${index}_${material.name}`] && (
                      <DeliveryAlternativesList
                        alternatives={materialDeliveryAlternatives[`${index}_${material.name}`]}
                        onSelectDelivery={(alternative) => handleMaterialDeliveryChange(index, alternative)}
                        colSpan={isOriginal ? 9 : 8}
                        setExpandedRows={() => {
                          setMaterialDeliveryAlternatives(prev => {
                            const newState = { ...prev };
                            delete newState[`${index}_${material.name}`];
                            return newState;
                          });
                        }}
                        currentDelivery={{
                          name: getMaterialDeliveryMethod(index),
                          totalCost: materialDeliveryData[material.name]?.totalCost || offer.delivery?.totalCost || 0,
                          numberOfTrips: materialDeliveryData[material.name]?.numberOfTrips || offer.delivery?.numberOfTrips || 1,
                          costPerTrip: materialDeliveryData[material.name]?.costPerTrip || offer.delivery?.costPerTrip || 0
                        }}
                        paymentMethod={paymentMethod}
                        isLoading={false}
                        parentTableStructure={true}
                        isOriginal={isOriginal}
                      />
                    )}
                    {/* Показываем аналоги песка */}
                    {showSandAnalogs && material.name.toLowerCase().includes('песок') && (
                      <SandAnalogsList
                        analogs={sandAnalogs[material.name] || []}
                        onSelectAnalog={(analog, materialName) => {
                          handleSelectAnalog(analog, materialName);
                          setShowSandAnalogs(false); // Скрываем список после выбора
                        }}
                        colSpan={isOriginal ? 9 : 8}
                        currentMaterial={material}
                        isLoading={isLoadingAnalogs[material.name] || false}
                        parentTableStructure={true}
                        isOriginal={isOriginal}
                      />
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rd-kp-totals">
            <div className="rd-kp-totals__label">
              {isOriginal ? 'К оплате' : 'Итого'}
            </div>
            <div className="rd-kp-totals__value">
              {Math.ceil(totalSum).toLocaleString('ru-RU')} ₽
            </div>
            <div className="rd-kp-totals__meta">
              {isOriginal
                ? <>{per.text} <b>{Math.abs(resultSkidkaOrMarzha(totalSum, baseSum, per.id)).toLocaleString('ru-RU')} ₽</b> · <b>{procentSkidkaOrMarzha(totalSum, baseSum, per.id)}%</b></>
                : <>Выгода <b>{Math.abs(resultSkidkaOrMarzha(totalSum, baseSum, per.id)).toLocaleString('ru-RU')} ₽</b></>
              }
            </div>
          </div>

          <div className="rd-kp-actions">
            <button
              className="rd-kp-actions__btn rd-kp-actions__btn--b24"
              onClick={() => onFillBitrix(offer)}
              title="Заполнить в сделке Bitrix"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M19.35 10.04C18.67 6.59 15.64 4.25 12 4.25c-2.89 0-5.4 1.64-6.65 4.04C2.34 8.61 0 11.16 0 14.25c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-5.21z" fill="white" />
                <text x="12" y="17.4" textAnchor="middle" fontSize="8.2" fontWeight="900" fill="#1f88c8" fontFamily="-apple-system,Inter,Arial,sans-serif" letterSpacing="-0.3">24</text>
              </svg>
            </button>
            <button
              className="rd-kp-actions__btn rd-kp-actions__btn--msg"
              onClick={() => onCreateMessage(offer)}
              title="Сформировать сообщение"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="white">
                <path d="M256 32C114.6 32 0 125.1 0 240c0 49.6 21.4 95 57 130.7C44.5 421.1 2.7 466 2.2 466.5c-2.2 2.3-2.8 5.7-1.5 8.7S4.8 480 8 480c66.3 0 116-31.8 140.6-51.4 32.7 12.3 69 19.4 107.4 19.4 141.4 0 256-93.1 256-208S397.4 32 256 32z" />
              </svg>
            </button>
            {/* <button
              className="rd-kp-actions__btn rd-kp-actions__btn--max"
              onClick={() => onSendToMax(offer)}
              title="Отправить в MAX"
            >
              <img src="https://maxicons.ru/icons/Max_logo_black.svg" alt="MAX" style={{ width: '60%', height: '60%', filter: 'brightness(0) invert(1)' }} />
            </button> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default OfferTableNonmetallic;